import React from 'react';
import { ButtonProps } from '../../../types';

// Button-specific constants
const BUTTON_SIZE_CLASSES = {
  small: {
    default: 'px-2 py-1.5 text-sm',
    iconOnly: 'p-2'
  },
  medium: {
    default: 'px-4 py-2 text-base',
    iconOnly: 'p-2'
  },
  large: {
    default: 'px-6 py-3 text-lg',
    iconOnly: 'p-3'
  }
} as const;

const BUTTON_ICON_SIZES = {
  small: 'w-4 h-4',
  medium: 'w-5 h-5',
  large: 'w-6 h-6'
} as const;

const BUTTON_ROUNDED_CLASSES = {
  'none': 'rounded-none',
  'sm': 'rounded-sm',
  'md': 'rounded-md',
  'lg': 'rounded-lg',
  'xl': 'rounded-xl',
  'full': 'rounded-full',
} as const;

const BUTTON_FONT_WEIGHT_CLASSES = {
  'light': 'font-light',
  'normal': 'font-normal',
  'medium': 'font-medium',
  'semibold': 'font-semibold',
  'bold': 'font-bold',
} as const;

export const Button: React.FC<ButtonProps> = ({
  children,
  onClick,
  type = 'button',
  variant = 'primary',
  size = 'medium',
  icon,
  iconPosition = 'right',
  disabled = false,
  className = '',
  isIconOnly = false,
  rounded = 'md',
  fontWeight = 'medium',
  isFullWidth = false,
}) => {
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return;
    onClick?.(event);
  };

  const sizeClasses = isIconOnly
    ? BUTTON_SIZE_CLASSES[size].iconOnly
    : BUTTON_SIZE_CLASSES[size].default;

  const iconClass = `${BUTTON_ICON_SIZES[size]} ${variant === 'primary' ? 'text-white' : 'text-current'}`;
  const IconElement = icon && React.cloneElement(icon as React.ReactElement, {
    className: iconClass
  });

  const getVariantClasses = (buttonVariant: any) => {
    switch (buttonVariant) {
      case 'primary':
        return 'bg-primary-700 text-white hover:bg-primary-800 active:bg-primary-900';
      case 'secondary':
        return 'bg-secondary-700 hover:bg-secondary-600 active:bg-secondary-600 text-tertiary-100 border border-secondary-400';
      case 'link':
        return 'bg-transparent border border-transparent font-kanit !p-0';
      case 'plain':
          return 'text-black border border-[#D0D5DD] shadow';
      case 'outline':
        return 'bg-transparent text-tertiary-50 hover:text-tertiary-200 active:text-secondary-700 border border-tertiary-50 hover:border-tertiary-200 active:border-secondary-100';
      case 'fill':
        return 'bg-white active:text-secondary-700 border border-transparent';
      case 'text-primary':
        return 'bg-white text-primary-700 hover:text-primary-800 active:text-primary-900 border border-transparent';
      default:
        return 'bg-primary-700 text-white hover:bg-primary-800 active:bg-primary-900';
    }
  };

  const baseClasses = [
    'whitespace-nowrap flex justify-center items-center gap-2',
    isIconOnly ? 'rounded-full active:scale-125' : BUTTON_ROUNDED_CLASSES[rounded],
    isIconOnly ? 'w-auto' : isFullWidth ? 'w-full' : 'w-auto',
    'transition-all duration-200',
    disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
    sizeClasses,
    BUTTON_FONT_WEIGHT_CLASSES[fontWeight],
    getVariantClasses(variant),
    className
  ].filter(Boolean).join(' ');

  return (
    <button
      type={type}
      onClick={handleClick}
      disabled={disabled}
      className={`${baseClasses} ${className}`}
    >
      {icon && iconPosition === 'left' && icon}
      {children}
      {icon && iconPosition === 'right' && icon}
    </button>
  );
};